import type { ApiError } from "./types";

export interface ApiRequestOptions {
  headers?: Record<string, string>;
  signal?: AbortSignal;
}

function classify(status: number): ApiError["code"] {
  if (status === 401) return "unauthorized";
  if (status === 403) return "forbidden";
  if (status === 404) return "not_found";
  if (status === 409) return "conflict";
  if (status === 429) return "rate_limited";
  if (status >= 500) return "server_error";
  return undefined;
}

export class ApiClient {
  readonly baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl.replace(/\/+$/, "");
  }

  async get<T>(path: string, opts: ApiRequestOptions = {}): Promise<T> {
    return this.request<T>("GET", path, undefined, opts);
  }

  async patch<T>(path: string, body: unknown, opts: ApiRequestOptions = {}): Promise<T> {
    return this.request<T>("PATCH", path, body, opts);
  }

  async post<T>(path: string, body?: unknown, opts: ApiRequestOptions = {}): Promise<T> {
    return this.request<T>("POST", path, body, opts);
  }

  async put<T>(path: string, body: unknown, opts: ApiRequestOptions = {}): Promise<T> {
    return this.request<T>("PUT", path, body, opts);
  }

  private async request<T>(
    method: "GET" | "PATCH" | "POST" | "PUT",
    path: string,
    body: unknown | undefined,
    opts: ApiRequestOptions,
  ): Promise<T> {
    const url = `${this.baseUrl}${path}`;
    const headers: Record<string, string> = { ...(opts.headers ?? {}) };
    let init: RequestInit = { method, headers, signal: opts.signal };
    if (body !== undefined) {
      headers["Content-Type"] = "application/json";
      init = { ...init, body: JSON.stringify(body) };
    }
    const res = await fetch(url, init);
    if (!res.ok) {
      let payload: unknown = undefined;
      try {
        payload = await res.json();
      } catch {
        // body was empty / non-JSON — leave payload undefined
      }
      const detail = (payload && typeof payload === "object" && "error" in (payload as object))
        ? String((payload as { error: unknown }).error)
        : "";
      const err: ApiError = {
        status: res.status,
        message: detail || `${res.status} ${res.statusText}`,
        code: classify(res.status),
        body: payload,
      };
      throw err;
    }
    return (await res.json()) as T;
  }
}
